import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { useAuth, usePermissions } from "../contexts";
import { useAxios, useGenericQuery, useGenericMutation } from "../hooks";
import {
    Account,
    MutationResult,
    QboAccount,
    QboAuthValuesRequest,
    QboCreateItemRequest,
    QboCustomer,
    QboInvoice,
    QboItem,
    QboPreferences,
    QboTerm,
    QueryResult,
    InvoiceSyncResponse,
    Claims,
    BillingAccountSyncResponse,
    QboClass,
} from "../models";

interface IUseQuickbooksQuery {
    syncUnpaidInvoicesQuery: QueryResult<InvoiceSyncResponse>;
    customerDetailQuery: QueryResult<QboCustomer>;
    customersQuery: QueryResult<QboCustomer[]>;
    invoiceDetailQuery: QueryResult<QboInvoice>;
    invoicesQuery: QueryResult<QboInvoice[]>;
    itemsQuery: QueryResult<QboItem[]>;
    itemDetailQuery: QueryResult<QboItem>;
    termsQuery: QueryResult<QboTerm[]>;
    accountsQuery: QueryResult<QboAccount[]>;
    preferencesQuery: QueryResult<QboPreferences>;
    classesQuery: QueryResult<QboClass[]>;
    latestInvoiceNumberQuery: QueryResult<string | undefined>;

    qboAuthValuesMutation: MutationResult<void, unknown, QboAuthValuesRequest>;
    createCustomerMutation: MutationResult<QboCustomer, unknown, Account>;
    createItemMutation: MutationResult<QboItem, unknown, QboCreateItemRequest>;
    syncBillingAccountMutation: MutationResult<BillingAccountSyncResponse>;
}

interface IUseQuickbooksQueryParams {
    queryTypes?: (
        | "class"
        | "customer"
        | "item"
        | "invoice"
        | "preferences"
        | "account"
        | "term"
        | "sync"
        | "latest-invoice-number"
    )[];
    id?: string;
}

export function useQuickbooksQuery(params?: IUseQuickbooksQueryParams): IUseQuickbooksQuery {
    const { isAuthorized } = useAuth();
    const { get, post, put } = useAxios();
    const { hasPermission } = usePermissions();
    const canEditInvoices = hasPermission(Claims.EditInvoice);
    const url = Endpoints.Quickbooks;
    const id = params?.id || "";
    const queryTypes = params?.queryTypes ?? [];

    const syncUnpaidInvoicesQuery = useGenericQuery({
        queryFn: (config) => get<InvoiceSyncResponse>({ url: `${url}/sync-invoices`, config }),
        queryKey: queryKeys.quickbooks.sync(),
        placeholderData: undefined,
        enabled: isAuthorized && queryTypes.includes("sync") && canEditInvoices,
        // TODO: if client wants to see invoice sync updates, uncomment this
        // onSuccess: (data) => {
        //     if (data.success) {
        //         const toast: KeysysToastProps = {
        //             name: "INVOICE_SYNC_SUCCESS",
        //             titleInHeader: "Successful Invoice Sync",
        //             body: data.message,
        //             theme: "success",
        //         };
        //         showToast(toast);
        //     } else {
        //         toast("Error syncing unpaid invoices");
        //     }
        // },
    });

    const customerDetailQuery = useGenericQuery({
        queryFn: (config) => get<QboCustomer>({ url: `${url}/customers/${params?.id}`, config }),
        queryKey: queryKeys.quickbooks.customer(id),
        placeholderData: undefined,
        enabled: isAuthorized && queryTypes.includes("customer") && !!params?.id,
    });

    const customersQuery = useGenericQuery({
        queryFn: (config) => get<QboCustomer[]>({ url: `${url}/customers`, config }),
        queryKey: queryKeys.quickbooks.customers(),
        placeholderData: [],
        enabled: isAuthorized && queryTypes.includes("customer") && !params?.id,
    });

    const invoiceDetailQuery = useGenericQuery({
        queryFn: (config) => get<QboInvoice>({ url: `${url}/invoices/${params?.id}`, config }),
        queryKey: queryKeys.quickbooks.invoice(id),
        placeholderData: undefined,
        enabled: isAuthorized && queryTypes.includes("invoice") && !!params?.id,
    });

    const invoicesQuery = useGenericQuery({
        queryFn: (config) => get<QboInvoice[]>({ url: `${url}/invoices`, config }),
        queryKey: queryKeys.quickbooks.invoices(),
        placeholderData: [],
        enabled: isAuthorized && queryTypes.includes("invoice") && !params?.id,
    });

    const itemsQuery = useGenericQuery({
        queryFn: (config) => get<QboItem[]>({ url: `${url}/items`, config }),
        queryKey: queryKeys.quickbooks.items(),
        placeholderData: [],
        enabled: isAuthorized && queryTypes.includes("item") && !params?.id,
    });

    const itemDetailQuery = useGenericQuery({
        queryFn: (config) => get<QboItem>({ url: `${url}/items/${params?.id}`, config }),
        queryKey: queryKeys.quickbooks.item(id),
        placeholderData: undefined,
        enabled: isAuthorized && queryTypes.includes("item") && !!params?.id,
    });

    const termsQuery = useGenericQuery({
        queryFn: (config) => get<QboTerm[]>({ url: `${url}/terms`, config }),
        queryKey: queryKeys.quickbooks.terms(),
        placeholderData: [],
        enabled: isAuthorized && queryTypes.includes("term"),
    });

    const accountsQuery = useGenericQuery({
        queryFn: (config) => get<QboAccount[]>({ url: `${url}/accounts`, config }),
        queryKey: queryKeys.quickbooks.accounts(),
        placeholderData: [],
        enabled: isAuthorized && queryTypes.includes("account"),
    });

    const classesQuery = useGenericQuery({
        queryFn: (config) => get<QboClass[]>({ url: `${url}/classes`, config }),
        queryKey: queryKeys.quickbooks.classes(),
        placeholderData: [],
        enabled: isAuthorized && queryTypes.includes("class"),
    });

    const preferencesQuery = useGenericQuery({
        queryFn: (config) => get<QboPreferences>({ url: `${url}/preferences`, config }),
        queryKey: queryKeys.quickbooks.preferences(),
        placeholderData: undefined,
        enabled: isAuthorized && queryTypes.includes("preferences"),
    });

    const latestInvoiceNumberQuery = useGenericQuery({
        queryFn: (config) =>
            get<{ latestNumber: string }>({ url: `${url}/latest-invoice-number`, config }).then(
                (res) => res.latestNumber ?? ""
            ),
        queryKey: queryKeys.quickbooks.latestInvoiceNumber(),
        placeholderData: undefined,
        enabled: isAuthorized && queryTypes.includes("latest-invoice-number"),
    });

    const qboAuthValuesMutation = useGenericMutation({
        mutationFn: (data: QboAuthValuesRequest) => put<QboAuthValuesRequest, void>({ url: `${url}/auth`, data }),
        queryKey: queryKeys.quickbooks.auth(),
        successToast: "QuickBooks auth values updated",
    });

    const createCustomerMutation = useGenericMutation({
        mutationFn: (data: Account) => post<Account, QboCustomer>({ url: `${url}/customers`, data }),
        queryKey: queryKeys.quickbooks.customers(),
        keysToInvalidate: [queryKeys.accounts.details(), queryKeys.accounts.pagedLists()],
        successToast: "Customer created",
    });

    const createItemMutation = useGenericMutation({
        mutationFn: (data: QboCreateItemRequest) => post<QboCreateItemRequest, QboItem>({ url: `${url}/items`, data }),
        queryKey: queryKeys.quickbooks.items(),
        successToast: "Item created",
    });

    const syncBillingAccountMutation = useGenericMutation({
        mutationFn: () => post<BillingAccountSyncResponse>({ url: `${url}/sync-billing` }),
        queryKey: queryKeys.quickbooks.customers(),
        keysToInvalidate: [queryKeys.accounts.all(), queryKeys.quickbooks.customers()],
        successToast: "Portal accounts synced",
    });

    return useMemo<IUseQuickbooksQuery>(
        () => ({
            syncUnpaidInvoicesQuery,
            customerDetailQuery,
            customersQuery,
            invoiceDetailQuery,
            invoicesQuery,
            itemsQuery,
            itemDetailQuery,
            termsQuery,
            accountsQuery,
            preferencesQuery,
            qboAuthValuesMutation,
            createCustomerMutation,
            createItemMutation,
            syncBillingAccountMutation,
            classesQuery,
            latestInvoiceNumberQuery,
        }),
        [
            syncUnpaidInvoicesQuery,
            customerDetailQuery,
            customersQuery,
            invoiceDetailQuery,
            invoicesQuery,
            itemsQuery,
            itemDetailQuery,
            termsQuery,
            accountsQuery,
            preferencesQuery,
            qboAuthValuesMutation,
            createCustomerMutation,
            createItemMutation,
            syncBillingAccountMutation,
            classesQuery,
            latestInvoiceNumberQuery,
        ]
    );
}
